<script context="module" lang="ts">
  type Offer = {
    slug: string;
    visibility_status: string; // visible | sold_out
    collect_only: boolean;
    restaurant_id: number;
    restaurant_name: string;
    price: number;
    old_price: number | null;
    deal_badge: string | null

    // my adds
    compositeSlug: string
    restaurant: { dynamic: RestaurantDynamic, semi_static: RestaurantSemiStatic };
  };

  type Item = {
    name: string;
    variant_name: string;
    image_url: string;
    restaurant_group_name: string;
    offers: Offer[]
    restaurant: { dynamic: RestaurantDynamic, semi_static: RestaurantSemiStatic };
  };

  type ItemSearch = {
    total_pages: number;
    message: string;
    results: Array<Item>;
  };

  export type ItemSearchParams = {
    term: string
    vertical: string;
    query: string;
  };

  const hasImg = (i: Item): boolean => i.image_url !== null;

  const online = (o: Offer): boolean => o.restaurant.dynamic.online === true && o.restaurant.dynamic.open === true;
  const offline = (o: Offer): boolean => o.restaurant.dynamic.online === false || o.restaurant.dynamic.open === false;

  const inStock = (o: Offer): boolean => o.visibility_status === "visible";
  const outStock = (o: Offer): boolean => o.visibility_status === "sold_out";

  const available = (o: Offer): boolean => online(o) && inStock(o);
  const unavailable = (o: Offer): boolean => offline(o) || outStock(o);

  const collectOnly = (o: Offer): boolean => o.collect_only === true;

  const deal = (o: Offer): boolean => o.deal_badge !== null;

  const halaal = (o: Offer): boolean => o.restaurant.dynamic.tag_string === "Halaal";
  const hasFoodTag = (o: Offer): boolean => ["Halaal", "Kosher"].includes(o.restaurant.dynamic.tag_string);

  const distance = (r: { distance: number }): number => Number((r.distance / 1000).toFixed(1));
  const preptime = (o: Offer): number => o.restaurant.dynamic.prep_time + o.restaurant.dynamic.estimated_delivery_time_for_distance;

  const rating =(o: Offer): number => o.restaurant.dynamic.statistics.avg_food_quality_rating;
  const totalRatings = (o: Offer): string => o.restaurant.dynamic.statistics.number_of_ratings_str.replace('(','').replace(')','');

  function availability(o: Offer): string {
    if (offline(o)) return "Offline";
    if (outStock(o)) return "Sold out";
    if (collectOnly(o)) return "Collect only";
    const t = preptime(o);
    return `${t}min`;
  }

  function sortOffers(offers: Offer[]): Offer[] {
    const _deliverable = [];
    const _collectOnly = [];
    const _soldOut = [];
    const _offline = [];

    for (const o of offers) {
      if (online(o) && inStock(o) && !collectOnly(o))
        _deliverable.push(o);

      if (online(o) && inStock(o) && collectOnly(o))
        _collectOnly.push(o);

      if (online(o) && outStock(o))
        _soldOut.push(o);

      if (offline(o))
        _offline.push(o);
    }
    _deliverable.sort((a, b) => distance(a.restaurant.dynamic) - distance(b.restaurant.dynamic))
    return _deliverable.concat(_collectOnly).concat(_soldOut).concat(_offline);
  }

  function polyFillItem(i: Item) {
    if (!i.restaurant_group_name) {
      i.restaurant_group_name = i.offers[0].restaurant_name;
    }
  }

  function randomiseOffer(o: Offer) {
    const r = randomInt(1, 100);
    const soldOut = randomInt(1, 100) <= 50;

    if (r <= 25) {
      o.old_price = randomInt(o.price, o.price * 1.25)
      o.visibility_status = soldOut ? "sold_out" : "visible";
    }
    if (r >= 26 && r <= 50) {
      o.old_price = randomInt(o.price, o.price * 1.5)
      o.collect_only = true;
    }
    if (r >= 51 && r <= 75) {
      o.deal_badge = "Free Delivery"
    }
    if (r >= 76) {
      o.deal_badge = "Buy 1 Get 1 Free"
    }
  }

  function polyFillRest(rest: RestaurantDynamic) {
    const r = randomInt(1, 100);
    const online = r >= 20;

    rest.distance = randomInt(1000, 10000);
    // rest.online = online;
    if (!rest.statistics || !rest.statistics.avg_food_quality_rating || !rest.statistics.number_of_ratings_str) {
      rest.statistics = {
        avg_food_quality_rating: randomFloat(1, 5, 1),
        number_of_ratings_str: randomInt(100, 10000).toString(),
        rating_star_colour: "",
      };
    }
  }
</script>

<script lang="ts">
  import type { RestaurantDynamic, RestaurantSemiStatic } from "$lib/def/landing";
  import { Vertical } from "$lib/def/verticals";
  import { post$ } from "$lib/serv/core/auth";
  import Grid from "$lib/ui/layout/Grid.svelte";
  import Illustration from "$lib/ui/layout/Illustration.svelte";
  import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
  import { formatCurrency } from "$lib/util/format";
  import { randomFloat, randomInt } from "$lib/util/func";
  import { query } from "$lib/util/url";
  import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, map, mergeScan, Observable, of, share, Subject, switchMap, tap } from "rxjs";
  import { onDestroy } from "svelte";
  import { restaurants$, shops$ } from "../FoodLanding.svelte";
  import { makeRestPDPSlug } from "./menu/server";
  import { navPDP } from "./ItemMerchGrid.svelte";

  export let searchParams: ItemSearchParams;
  $: {
    _searchParams$.next(searchParams)
  }

  const _searchParams$ = new Subject<ItemSearchParams>();
  const searchParams$ = _searchParams$.pipe(
    distinctUntilChanged((prev, cur) => {
      return prev.term === cur.term && prev.vertical === cur.vertical;
    }),
  );

  const subParams = searchParams$.subscribe(async (params) => {
    // console.log("searchParams$", params);
    page$.next(1);
  });
  
  let init = true;
  let loading = true;

  const page$ = new BehaviorSubject(1);
  
  type MerchantsData = {
    dynamic: Map<number, RestaurantDynamic>;
    semi_static: Map<number, RestaurantSemiStatic>;
  };

  const itemSearch$ = combineLatest([searchParams$, page$]).pipe(
    debounceTime(100),
    tap(([_params, page]) => {
      init = page === 1;
      loading = true;
    }),
    switchMap(([params, page]) => {
      return (params.vertical === Vertical.Restaurant ? restaurants$ : shops$).pipe(
        map(merchants => ({params, page, merchants}))
      )
    }),
    switchMap(({params, page, merchants}) => {
      const ids = Array.from(merchants.dynamic.keys());

      if (params.term.length < 3 || ids.length === 0) {
        return of({ total_pages: 1, message: "empty", results: [] });
      }

      const body = { text: params.term, vertical: params.vertical, page, ids };

      return post$<ItemSearch>("api://search/item/v2", body).pipe(
        map(res => ({ ...res, merchants }))
      );
    }),
    catchError((err) => {
      console.error('error', err);
      return of({ total_pages: 1, message: "error", results: [] });
    }),
    share(),
    // tap((res) => console.log("itemSearch$ emit -- ", res)),
  ) as Observable<ItemSearch & { merchants?: MerchantsData }>;

  const newItems$: Observable<Item[]> = itemSearch$.pipe(
    // tap(v => console.log('newItems$')),
    map(({results, merchants}) =>  {
      for (const item of results) {
        for (const offer of item.offers) {
          const rest = {
            dynamic: merchants?.dynamic.get(offer.restaurant_id),
            semi_static: merchants?.semi_static.get(offer.restaurant_id),
          };

          if (rest.dynamic) {
            polyFillRest(rest.dynamic);
            polyFillItem(item);
            // randomiseOffer(offer);
            offer.compositeSlug = makeRestPDPSlug(offer.slug, "", rest.dynamic.menu_id, rest.dynamic.id);
            offer.restaurant = rest as any;
          } else {
            // the server returned IDs not given to it
            // without setting `offer.restaurant` we'll filter these out later
          }
        } 
        item.offers = sortOffers(item.offers.filter(o => o.restaurant));
      }
      return results.filter(res => res.offers.length > 0);
    }),
    // tap((res) => console.log("newItems$ emit -- ", res)),
  );

  const allItems$: Observable<Item[]> = newItems$.pipe(
    mergeScan((acc, items) => page$.value === 1 ? of(items) : of(acc.concat(items)), [] as Item[]),
    tap(() => {
      init = false;
      loading = false;
    }),
    // tap((res) => console.log("allItems$ emit -- ", res)),
  );

  let scrollY: number;
  let scrollHeight: number;
  // const scroll$ = fromEvent(document.getElementById("modal-body") as HTMLElement, "scroll");
  const scroll$ = new Subject<number>();
  const scrollHeight$ = new Subject<number>();
  $: scroll$.next(scrollY);
  $: scrollHeight$.next(scrollHeight);

  const pagination$ = combineLatest([itemSearch$, scroll$, scrollHeight$]);

  const subPag = pagination$.subscribe(([itemSearch, scroll, scrollHeight]) => {
    // console.log("pagination$ emit -- ", { itemSearch, scroll });
    if (init || loading || $page$ === itemSearch.total_pages) {
      return;
    }

    // const modalBody = scroll.target as HTMLElement;

    if (scroll >= scrollHeight) {
      // console.log('page added.');
      page$.next($page$ + 1);
    }
  });

  let offers: Offer[] | null = null;
  function onItemClick(item: Item, params: ItemSearchParams) {
    if (item.offers.length > 1) {
      offers = item.offers;
      return;
    }
    navPDP(item.offers[0], new URLSearchParams(params.query));
  }

  function onOfferClick(offer: Offer, params: ItemSearchParams) {
    navPDP(offer, new URLSearchParams(params.query))
  }

  const toArr = (items: Item[] | undefined) => (items ? items : []) as Item[] ;

  const allItemsSub = allItems$.subscribe();
  onDestroy(() => {
    subParams.unsubscribe();
    subPag.unsubscribe();
    allItemsSub.unsubscribe()
  });

</script>

<svelte:window bind:scrollY bind:innerHeight={scrollHeight} />

<!-- DEBUG -->
<!--<div style="position: fixed; z-index: 10; background-color: black;">
  <h1 class="headline-m">page {$page$}</h1>
  <h2 class="headline-m">total {$allItems$?.length}</h2>
  <h2 class="headline-m">init {init}</h2>
  <h2 class="headline-m">loading {loading}</h2>
</div>-->
<!-- <h1 class="headline-m"></h1> -->

<!-- NO RESULTS -->
{#if !loading && $allItems$.length === 0}
	<div class="flex mobile align-center justify-start">
    <span style="max-width: 150px;">
      <Illustration src='general-error-banana-medium' />
    </span>
      {#if searchParams.term === ''}
			  <div style="text-align: left;" class="headline-s bold">Please enter a search above...</div>
      {:else}
			<article>
        <div class="headline-s bold" style="text-align: left;">We couldn't find anything for "{searchParams.term}"...</div>
        <div class="headline-s bold" style="text-align: left;">
          Try the toggle
          <span class="desktop">on the left</span>
          <span class="mobile">below</span>
          to search {searchParams.vertical === Vertical.Restaurant ? 'restaurants' : 'shops'}.
        </div>
      </article>
      {/if}
	</div>
{/if}

<!-- RESULTS -->
<Grid
  cache={{ loading: init, payload: $allItems$ }}
  type="aisle"
  let:item
  key={(v) => toArr(v)}
>
  <!-- HAS IMAGE -->
  {#if hasImg(item)}
	  <button on:click={() => onItemClick(item, searchParams)} class="item" class:unavailable={unavailable(item.offers[0])}>
      <!--<span>{item.offers.length}</span>-->
      <LazyBackgroundImage image={[item.image_url, 200]} alt={item.name}>
        {#if unavailable(item.offers[0])}
	  	    <article class="cover"/>
        {/if}
	  	  <span class="availability-pill label-xs bold">{availability(item.offers[0])}</span>
      </LazyBackgroundImage>
		  {#if item.offers[0].old_price}
		  	<div class="flex-row">
		  		<span style="margin-right: 8px;" class="label-l bold m-0"><!--{item.offers.length > 1 ? '*' : ''}-->{formatCurrency(Number(item.offers[0].price))}</span>
		  		<span class="label-l medium m-0 medium-importance"><s>{formatCurrency(Number(item.offers[0].old_price))}</s></span>
		  	</div>
		  	{:else}
		  	<div><span class="label-l bold m-0"><!--{item.offers.length > 1 ? '*' : ''}-->{formatCurrency(Number(item.offers[0].price))}</span></div>
		  {/if}
      {#if deal(item.offers[0])}
        <div class="label-xs bold m-0 color-promo">{item.offers[0].deal_badge}</div>
      {/if}
	  	<div class="label-m book m-0 mt-6px cut-text">{item.name}</div>
	  	<div class="label-s bold ellipses m-0 mt-4px">{item.restaurant_group_name}</div>
      {#if hasFoodTag(item.offers[0])}
	  	  <div class="label-s book m-0 ">{item.offers[0].restaurant.dynamic.tag_string}</div>
      {/if}
	  </button>
  {/if}

  <!-- NO IMAGE -->
  {#if !hasImg(item)}
	  <button on:click={() => onItemClick(item, searchParams)} class="item no-image" class:unavailable={unavailable(item.offers[0])}>
      <!--<span>{item.offers.length}</span>-->
      <div class="lebel-l bold">{formatCurrency(Number(item.offers[0].price))}</div>
      {#if deal(item.offers[0])}
        <div class="label-xs bold m-0 color-promo">{item.offers[0].deal_badge}</div>
      {/if}
	  	<div class="label-m book m-0 mt-6px">{item.name}</div>
      <div class="label-s bold ellipses m-0 mt-4px">{item.restaurant_group_name}</div>
      {#if hasFoodTag(item.offers[0])}
	  	  <div class="label-s book m-0 ">{item.offers[0].restaurant.dynamic.tag_string}</div>
      {/if}
	  	<div style="margin-top: 12px;"><span class="availability-pill label-xs bold">{availability(item.offers[0])}</span></div>
	  </button>
  {/if}
</Grid>

{#if offers}
 <div class="backdrop" role="button" tabindex="0" on:click={() => offers = null} on:keypress>
    <div class="offers-container">
      <h3 class="headline-m bold text-center">Select a {searchParams.vertical === Vertical.Restaurant ? "Restaurant" : "Shop"}</h3>
      <ul class="offers">
        {#each offers as offer}
          <li>
            <button class="offer" class:unavailable={unavailable(offer)} on:click={() => onOfferClick(offer, searchParams)}>
              <div style="margin-right: 32px;">
                <div>
                  <span class="label-xl bold ellipses  m-0">{offer.restaurant_name}</span>
                </div>
                {#if collectOnly(offer)}
                  <div><span class="label-m medium">Collect Only</span></div>
                {/if}
                <div class="flex-row align-center medium-importance">
                  <span>{rating(offer)}</span>
                  <span class="icon-star-20"></span>
                  <span>({totalRatings(offer)})</span>
                  <span class="dot"></span>
                  <span>({distance(offer.restaurant.dynamic)} km)</span>
                  {#if hasFoodTag(offer)}
                    <span class="dot"></span>
                    <span>{offer.restaurant.dynamic.tag_string}</span>
                  {/if}
                </div>
              </div>
              <div>
                {#if available(offer)}
                  <span class="icon-chevron-right-20"></span>
                {:else}
                  <span class="availability-pill label-xs bold m-0">{availability(offer)}</span>
                {/if}
              </div>
            </button>
          </li>
        {/each}
      </ul>
    </div>
 </div> 
{/if}

{#if !init && loading}
  <div class="flex-row justify-center"><span class="spinner"></span></div>
{/if}

<style lang="scss">
  @use "screen";
  @use "color";

  button {
    text-align: left;
  }

  span {
    &.desktop {
      @media (screen.$max-md) {
        display: none;
      }
    }
    &.mobile {
      @media (screen.$md) {
        display: none;
      }
    }
  }

  .cut-text {
    max-height: 65px;
    white-space: break-spaces;
	  overflow: hidden;
	  text-overflow: ellipsis;
  }

  .mt-6px {
    margin-top: 6px !important;
  }

  .mt-4px {
    margin-top: 4px !important;
  }

  .color-promo {
    color: color.$promo;
  }

	.cover {
    border-radius: inherit;
		position: absolute;
    top: 0;
    left: 0;
		width: 100%;
		height: 100%;
		background-color: color.transparent(0.7);
		z-index: 1;
	}

  // added :global() to remove 'npm run check' warnings
  :global(.item), .offer {
    &.unavailable {
      color: color.$on-background-low-importance;
      .color-promo {
        color: color.$on-background-low-importance;
      }
    }
  }


  .item:not(.no-image) {
    .availability-pill {
      position: absolute;
      bottom: 5px;
      left: 5px;
    }
  }

  .item:is(.no-image) {
    border: 2px solid color.$separator;
    border-radius: 25px;
    padding: 15px;

    &:hover {
      border: 2px solid color.$on-background-high-importance;
    }
  }

  .availability-pill {
    background-color: color.$white;
    color: color.$black;
    border: 0.5px solid color.$separator;
    border-radius: 10px;
    padding: 2px 5px;
    z-index: 1;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 3;

    display: flex;
    align-items: center;
    justify-content: center;

    height: 100%;
    width: 100%;
    // background-color: cyan;
  }

  .offers-container {
    background-color: color.$background-elevation-12dp;
    border-radius: 34px;
    padding: 32px;
    @media (screen.$max-sm) {
      padding: 16px;
    }

  // box-shadow: 0px 8px 10px 0px rgba(0, 0, 0, 0.2);
    // box-shadow: 0px 6px 30px 0px rgba(0, 0, 0, 0.12);
    box-shadow: 0px 16px 24px 0px rgba(0, 0, 0, 0.14);
  }

  ul.offers {
    li {
      list-style: none;
      margin-bottom: 12px;
      &:last-child {
        margin-bottom: 0;
      }
    }

    button.offer {
      display: flex;
      align-items: center;
      justify-content: space-between;

      background-color: color.$object-elevation-3dp;
      border-radius: 14px;
      padding: 22px 16px;
      width: 100%;
      min-width: 400px;
      max-width: 500px;


      &:hover {
        outline: 2px solid color.$on-background-high-importance;
        cursor: pointer;
      }
      &>*:hover {
        cursor: pointer;
      }
    }
  }

  .icon-star-20 {
    margin: 0 3px;
    transform: translateY(2px);
  }

  .dot {
    display: inline-block;
    background-color: color.$on-background-low-importance;
    border-radius: 50%;
    height: 4px;
    width: 4px;
    margin: 0 6px;
  }

  .bg-low-importance {
    background-color: color.$on-background-low-importance;
  }

  .txt-low-importance {
    color: color.$on-background-low-importance;
  }
</style>
